import { useMemo } from "react"
import { type ColDef, type IAggFuncParams, type ICellRendererParams } from "ag-grid-community"
import { type IVariantSummary } from "~/hooks/useVariantTableData"
import { CollectionTableColumn } from "~/constants/collections"
import { formatDecimalToPercent, formatNumber, formatNumberWithSuffix } from "~/utils/formatting"
import { type ICollectionSimple } from "~/hooks/useCollections"
import { type CollectionTableData } from "./useCollectionTableData"
import { type CollectionComparisonTableData } from "~/hooks/useCollectionComparisonTableData"
import type { PerCollectionComparisonEntry } from "~/hooks/usePerCollectionComparisonData"
import { createOutColumnHelpers, formatOutDisplay, getOutStatus } from "~/utils/outColumnUtils"
import { getFilteredLeafChildren, getFilteredLeafData } from "~/utils/ag-utils"
import { getTailwindValue } from "~/utils/tailwind"
import { useLiveReader, type VersionedColumns } from "~/hooks/useRefreshColumnsOnDataChange"
import { formatName } from "~/utils/namingUtils"
import { DateFormatType, formatDate, startOfTodayUTC } from "~/utils/datetime"
import { subWeeks } from "date-fns"
import {
  type ColumnTemplateConfig,
  createStringColumn,
  createSumColumn,
  createUniqueStringColumn,
} from "~/utils/columnTemplates"
import { api } from "~/utils/api"
import IncomingOrdersCollectionTooltip from "~/components/reusable/table/tooltips/IncomingOrdersCollectionTooltip"
import CollectionComparisonTooltip from "~/components/reusable/table/tooltips/CollectionComparisonTooltip"
import ComparisonMatchHoverCard from "~/components/collections/ComparisonMatchHoverCard"
import ComparisonCollectionHoverCard from "~/components/collections/ComparisonCollectionHoverCard"
import { excludeImputedComparison } from "~/utils/resolveComparisonVariantMatches"
import type { DynamicColumnId } from "~/components/planning/ProductTable/utils/columnUtils"

const ValueWithPercentageIndicator = ({
  value,
  comparisonValue,
  hasComparison,
  hidePercentage,
  formatValue = (v) => formatNumber(v, 0),
}: {
  value: number
  comparisonValue: number
  hasComparison: boolean
  hidePercentage?: boolean
  formatValue?: (v: number) => string
}) => {
  if (!hasComparison || comparisonValue === 0 || hidePercentage) {
    return <span className="w-full block">{formatValue(value)}</span>
  }

  const percentageDiff = (value - comparisonValue) / comparisonValue

  return (
    <div className="flex items-center gap-2">
      <span>{formatValue(value)}</span>
      <span
        className={`text-xs font-medium ${
          percentageDiff > 0
            ? "text-green-600"
            : percentageDiff < 0
              ? "text-red-600"
              : "text-fg-muted"
        }`}
      >
        {percentageDiff > 0 ? "+" : ""}
        {formatDecimalToPercent(percentageDiff, 0)}
      </span>
    </div>
  )
}

// Shared getter type — works with both CollectionTableData and CollectionComparisonTableData
type MetricGetter = (
  data: CollectionTableData | CollectionComparisonTableData | undefined,
  variantId: string,
) => number

const calculateAggregatedComparison = (
  params: ICellRendererParams<IVariantSummary>,
  getOriginalValue: (variantId: string) => number,
  getComparisonValue: (variantId: string) => number,
): { originalTotal: number; comparisonTotal: number } => {
  let originalTotal = 0
  let comparisonTotal = 0
  for (const child of getFilteredLeafChildren<IVariantSummary>(params.node)) {
    const variantId = child.data?.variantId
    if (!variantId) continue
    originalTotal += getOriginalValue(variantId)
    comparisonTotal += getComparisonValue(variantId)
  }
  return { originalTotal, comparisonTotal }
}

const projectedSalesGetter: MetricGetter = (data, variantId) =>
  (data?.salesMap?.get(variantId)?.sales ?? 0) + (data?.forecastMap?.get(variantId) ?? 0)

const forecastGetter: MetricGetter = (data, variantId) => data?.forecastMap?.get(variantId) ?? 0

// Ended comparison collections have no remaining forecast; their demand is the committed future
// sales orders, as on the planned side (getDemand) and the overview tile (sumComparisonDemand).
const endedFutureOrdersGetter: MetricGetter = (data, variantId) =>
  data && "endedFuturePreOrdersMap" in data ? (data.endedFuturePreOrdersMap.get(variantId) ?? 0) : 0

const comparisonDemandGetter: MetricGetter = (data, variantId) =>
  forecastGetter(data, variantId) + endedFutureOrdersGetter(data, variantId)

const comparisonTotalDemandGetter: MetricGetter = (data, variantId) =>
  projectedSalesGetter(data, variantId) + endedFutureOrdersGetter(data, variantId)

const salesGetter: MetricGetter = (data, variantId) => data?.salesMap?.get(variantId)?.sales ?? 0

const preOrdersGetter: MetricGetter = (data, variantId) =>
  data?.preOrderMap?.get(variantId)?.value ?? 0

// Main pre-orders column = standard + inbound. Sales Risk intentionally keeps
// preOrdersGetter (standard only), so this is scoped to the display column.
const preOrdersWithInboundGetter: MetricGetter = (data, variantId) => {
  const pv = data?.preOrderMap?.get(variantId)
  return (pv?.value ?? 0) + (pv?.inboundPreorderAmount ?? 0)
}

const ordersGetter: MetricGetter = (data, variantId) => data?.ordersMap?.get(variantId) ?? 0

const inboundPreOrdersGetter: MetricGetter = (data, variantId) =>
  data?.preOrderMap?.get(variantId)?.inboundPreorderAmount ?? 0

const preOrdersWithoutInboundGetter: MetricGetter = (data, variantId) =>
  data?.preOrderMap?.get(variantId)?.preorders ?? 0

const futurePreOrdersGetter: MetricGetter = (data, variantId) =>
  data?.preOrderMap?.get(variantId)?.futurePreorders ?? 0

const salesRiskGetter: MetricGetter = (data, variantId) =>
  salesGetter(data, variantId) - preOrdersGetter(data, variantId)

type UseCollectionTableColumnsProps = {
  maps: CollectionTableData
  comparisonMap?: CollectionComparisonTableData
  perCollectionComparisonData?: PerCollectionComparisonEntry[]
  comparisonCollections?: ICollectionSimple[]
  showInDepthColumns: boolean
  originalName?: string
  variantsMap?: Map<string, string>
  collectionIds?: number[]
  budgetMap?: Map<string, number>
  hasBudget?: boolean
  includeComparisonOnlyVariants?: boolean
  isNoos?: boolean
}

/** The data the columns read when AG Grid evaluates them, never when they are built. */
export type CollectionLiveData = {
  maps: CollectionTableData
  out: ReturnType<typeof createOutColumnHelpers>
  comparisonMap: CollectionComparisonTableData | undefined
  /** Index-aligned with the comparison columns. */
  comparisonData: (CollectionComparisonTableData | undefined)[]
  effectiveVariantsMap: Map<string, string> | undefined
  budgetMap: Map<string, number> | undefined
}

type ComparisonEntryMeta = Pick<PerCollectionComparisonEntry, "id" | "name">

export type CollectionColumnsStructure = {
  comparisonEntries: ComparisonEntryMeta[]
  plannedCollectionIds: number[] | undefined
  comparisonCollectionIds: number[]
  showInDepthColumns: boolean
  comparisonName: string
  originalName: string | undefined
  baselineComparisonTooltipNames: string | undefined
  currency: string
  returnsEnabled: boolean
  isNoos: boolean
  hasBudget: boolean | undefined
  includeComparisonOnlyVariants: boolean
}

/**
 * Builds the collection columns from structural inputs only. Every value, renderer and tooltip
 * reads its data through `live` when AG Grid evaluates it, so new data needs a repaint, not a
 * rebuild.
 */
export function buildCollectionColumns(
  {
    comparisonEntries,
    plannedCollectionIds,
    comparisonCollectionIds,
    showInDepthColumns,
    comparisonName,
    originalName,
    baselineComparisonTooltipNames,
    currency,
    returnsEnabled,
    isNoos,
    hasBudget,
    includeComparisonOnlyVariants,
  }: CollectionColumnsStructure,
  live: () => CollectionLiveData,
): ColDef<IVariantSummary>[] {
  const hasComparison = comparisonCollectionIds.length > 0
  const collectionLabel = originalName ?? "Collection period"
  const baselineComparisonName = comparisonEntries[0]?.name ?? comparisonName
  const plannedCollectionIdSet = plannedCollectionIds?.length ? new Set(plannedCollectionIds) : null
  const bothCollectionIdSet = new Set([...(plannedCollectionIds ?? []), ...comparisonCollectionIds])

  const maps = () => live().maps
  const comparison = () => live().comparisonMap
  const getOutData = (variantId: string | undefined) => live().out.getOutData(variantId)
  const aggregateOutData = (leafChildren: unknown[]) => live().out.aggregateOutData(leafChildren)

  const comparisonBg = getTailwindValue("--background-color-medium")

  // With comparison-only variants as rows, group sums skip match-imputed values.
  const groupComparison = (getComparison: (id: string) => number) =>
    includeComparisonOnlyVariants
      ? excludeImputedComparison(comparison()?.matchedVariantsSet, getComparison)
      : getComparison

  const wrapWithMatchHoverCard = (
    data: CollectionComparisonTableData | undefined,
    variantId: string | undefined,
    children: React.ReactNode,
    comparisonValue?: number,
    currentValue?: number,
    getMatchValue?: (matchId: string) => number,
    valueLabel?: string,
  ): React.ReactNode => {
    if (!variantId || !data?.matchedVariantsSet.has(variantId)) return children
    if (data.directVariantsSet.has(variantId)) return children
    const matches = data.contributingMatchesMap.get(variantId)
    if (!matches?.length) return children
    return (
      <ComparisonMatchHoverCard
        matches={matches}
        allMatches={data.allMatchesMap.get(variantId)}
        getMatchValue={getMatchValue}
        currentValue={currentValue}
        comparisonValue={comparisonValue}
        valueLabel={valueLabel}
      >
        {children}
      </ComparisonMatchHoverCard>
    )
  }

  // Generates one comparison column per comparison entry.
  // Each column reads its entry's data for values and hover card decisions.
  const makeComparisonColumns = (
    baseColId: CollectionTableColumn,
    headerName: string,
    getValue: MetricGetter,
    getMatchValue?: (data: CollectionComparisonTableData, id: string) => number,
    extraProps?: (
      entryData: () => CollectionComparisonTableData | undefined,
    ) => Partial<ColumnTemplateConfig<IVariantSummary>>,
  ): ColDef<IVariantSummary>[] =>
    comparisonEntries.map((entry, index) => {
      const entryData = () => live().comparisonData[index]
      return createSumColumn({
        colId: (index === 0 ? baseColId : `${baseColId}::${entry.id}`) as DynamicColumnId,
        headerName,
        headerSubtitle: entry.name,
        headerTooltip: `${headerName} for ${entry.name}`,
        minWidth: 150,
        valueGetter: (p: any) => getValue(entryData(), p.data?.variantId),
        cellStyle: { textAlign: "right", backgroundColor: comparisonBg },
        enableCellChangeFlash: false,
        cellRenderer: (params: ICellRendererParams) => {
          if (params.node?.group) return <span>{formatNumber(params.value ?? 0, 0)}</span>
          const variantId = params.data?.variantId
          const value = params.value ?? 0
          const label = <span>{formatNumber(value, 0)}</span>
          if (!variantId) return label
          const data = entryData()
          if (data?.directVariantsSet?.has(variantId)) {
            return (
              <ComparisonCollectionHoverCard value={value} collectionName={entry.name} inCollection>
                {label}
              </ComparisonCollectionHoverCard>
            )
          }
          if (data?.matchedVariantsSet?.has(variantId)) {
            const matchGetter = getMatchValue ? (id: string) => getMatchValue(data, id) : undefined
            return wrapWithMatchHoverCard(
              data,
              variantId,
              label,
              value,
              undefined,
              matchGetter,
              headerName,
            )
          }
          return (
            <ComparisonCollectionHoverCard
              value={value}
              collectionName={entry.name}
              inCollection={false}
            >
              {label}
            </ComparisonCollectionHoverCard>
          )
        },
        ...(includeComparisonOnlyVariants && {
          aggFunc: (params: IAggFuncParams<IVariantSummary>) => {
            const data = entryData()
            const getComparison = excludeImputedComparison(data?.matchedVariantsSet, (id) =>
              getValue(data, id),
            )
            let total = 0
            for (const { variantId } of getFilteredLeafData(params.rowNode)) {
              total += getComparison(variantId)
            }
            return total
          },
        }),
        ...extraProps?.(entryData),
      })
    })

  // Factory for "original vs comparison" cell renderers: shows % diff and wraps matched variants.
  // Used by columns where both original and comparison values are shown side-by-side.
  function makeMetricCellRenderer(
    getOriginal: (id: string) => number,
    getComparison: (id: string) => number,
    valueLabel?: string,
    formatValue?: (v: number) => string,
    getMatchComparison: (id: string) => number = getComparison,
  ) {
    return function MetricCellRenderer(params: ICellRendererParams) {
      const isGroup = params.node?.group
      const value = params.value ?? 0
      const isGroupRow = !!params.node.group
      const comparisonValue = isGroup
        ? calculateAggregatedComparison(params, getOriginal, groupComparison(getComparison))
            .comparisonTotal
        : params.data?.variantId
          ? getComparison(params.data.variantId)
          : 0
      const content = (
        <ValueWithPercentageIndicator
          value={value}
          comparisonValue={comparisonValue}
          hasComparison={hasComparison}
          hidePercentage={isGroupRow && !includeComparisonOnlyVariants}
          formatValue={formatValue}
        />
      )
      return isGroup
        ? content
        : wrapWithMatchHoverCard(
            comparison(),
            params.data?.variantId,
            content,
            comparisonValue,
            value,
            getMatchComparison,
            valueLabel,
          )
    }
  }

  // Factory for CollectionComparisonTooltip value getters. Suppresses tooltip on matched leaf rows.
  function makeMetricTooltipGetter(
    getOriginal: (id: string) => number,
    getComparison: (id: string) => number,
    metricLabel: string,
  ) {
    return (p: any) => {
      if (!p.node || (!p.data && !p.node.group)) return undefined
      if (
        !p.node.group &&
        p.data?.variantId &&
        comparison()?.matchedVariantsSet.has(p.data.variantId)
      )
        return undefined
      let originalValue = 0
      let compValue = 0
      if (p.node.group) {
        const getGroupComparison = groupComparison(getComparison)
        for (const { variantId } of getFilteredLeafData<IVariantSummary>(p.node)) {
          originalValue += getOriginal(variantId)
          compValue += getGroupComparison(variantId)
        }
      } else {
        const variantId = p.data?.variantId
        originalValue = variantId ? getOriginal(variantId) : 0
        compValue = variantId ? getComparison(variantId) : 0
      }

      const isGroupRow = !!p.node.group
      return {
        originalValue,
        comparisonValue: compValue,
        originalName: collectionLabel,
        comparisonName,
        metricLabel,
        hidePercentage: isGroupRow && !includeComparisonOnlyVariants,
      }
    }
  }

  // Demand = remaining forecast, plus future sales orders for ended collections (forecast is
  // 0 there). futureSalesOrdersMap is already scoped to ended collections. Total demand then
  // follows as sales + demand. The "without pre-orders" column keeps using projectedSalesGetter
  // (sales + forecast, no future orders) so it stays organic.
  const getDemand = (id: string | undefined) =>
    (maps()?.forecastMap?.get(id as string) || 0) +
    (maps()?.futureSalesOrdersMap?.get(id as string) || 0)

  // NOT Demand 2.0's `totalDemand`, though both render under a "Total demand" header: this is realised
  // sales PLUS remaining, whole-variant, off the legacy forecast/sales queries. Demand 2.0's is remaining
  // planned demand for the collection window and is scope-aware. See docs/helix/FORECAST_ENGINE.md § 11.
  const getTotalDemand = (id: string | undefined) =>
    (maps()?.salesMap?.get(id as string)?.sales || 0) + getDemand(id)

  const defaultColumns: ColDef<IVariantSummary>[] = [
    createStringColumn({
      colId: CollectionTableColumn.Out,
      minWidth: 100,
      filter: "agSetColumnFilter",
      valueGetter: (p: any) => {
        const { outCount, productionCount } = getOutData(p.data?.variantId)
        return getOutStatus(outCount, productionCount)
      },
      cellRenderer: (params: ICellRendererParams) => {
        const { group } = params.node

        if (!group) {
          const { outCount, productionCount } = getOutData(params.data?.variantId)
          return <span className="w-full block">{formatOutDisplay(outCount, productionCount)}</span>
        }

        const { outCount, totalProductionCount } = aggregateOutData(
          getFilteredLeafChildren(params.node),
        )
        return (
          <span className="w-full block">{formatOutDisplay(outCount, totalProductionCount)}</span>
        )
      },
      tooltipComponentParams: {
        title: "SKUs removed from collection",
        tooltipType: "UniqueList",
      },
      tooltip: (params) => {
        if (!params.node?.group) {
          return getOutData(params.data?.variantId).outSkuIds
        }
        return aggregateOutData(getFilteredLeafChildren(params.node)).outSkuIds
      },
    }),
    createUniqueStringColumn({
      colId: CollectionTableColumn.BelongingToCollection,
      valueGetter: (p: any) => {
        const allCollections = p.data?.collections as { id: string; name: string }[] | undefined
        const relevant = plannedCollectionIdSet
          ? allCollections?.filter((c) => plannedCollectionIdSet.has(Number(c.id)))
          : allCollections

        // In comparison mode, classify variant as original/comparison/both
        const { effectiveVariantsMap } = live()
        if (hasComparison && effectiveVariantsMap !== undefined && effectiveVariantsMap.size > 0) {
          const type = effectiveVariantsMap.get(p.data?.variantId)
          if (type === "comparison") return comparisonName
          if (type === "both") {
            const bothCollections = allCollections?.filter((c) =>
              bothCollectionIdSet.has(Number(c.id)),
            )
            if (bothCollections?.length) return bothCollections.map((c) => c.name)
            return "Both"
          }
          // "original" — return array so valueFormatter + tooltip both work correctly
          if (relevant?.length) return relevant.map((c) => c.name)
          return originalName
        }

        // Without comparison, show collection names filtered to the plan's collections
        return relevant?.map((c) => c.name) ?? null
      },
      tooltipTitle: "Collections",
      filter: true,
    }),

    createSumColumn({
      colId: CollectionTableColumn.ProjectedSales,
      headerSubtitle: hasComparison
        ? `${collectionLabel} vs ${baselineComparisonName}`
        : collectionLabel,
      headerTooltip: hasComparison
        ? `A combination of gross ${formatName("sale", true)} and demand. Percentage shows difference compared to ${baselineComparisonTooltipNames}.`
        : `A combination of gross ${formatName("sale", true)} and demand.`,
      minWidth: hasComparison ? 170 : 150,
      valueGetter: (p: any) => getTotalDemand(p.data?.variantId),
      cellRenderer: makeMetricCellRenderer(
        (id) => getTotalDemand(id),
        (id) => comparisonTotalDemandGetter(comparison(), id),
        `Total demand`,
        undefined,
        // Match imputation covers sales and forecast only, so the breakdown stays on that basis.
        (id) => projectedSalesGetter(comparison(), id),
      ),
      tooltipComponent: hasComparison ? CollectionComparisonTooltip : undefined,
      tooltip: hasComparison
        ? makeMetricTooltipGetter(
            (id) => getTotalDemand(id),
            (id) => comparisonTotalDemandGetter(comparison(), id),
            `Total demand`,
          )
        : undefined,
      enableCellChangeFlash: false,
    }),
    ...makeComparisonColumns(
      CollectionTableColumn.ProjectedSalesComparison,
      `Total demand`,
      comparisonTotalDemandGetter,
      projectedSalesGetter,
    ),
    createSumColumn({
      colId: CollectionTableColumn.Forecast,
      headerSubtitle: hasComparison ? originalName : collectionLabel,
      minWidth: 150,
      valueGetter: (p: any) => getDemand(p.data?.variantId),
      enableCellChangeFlash: false,
    }),
    ...makeComparisonColumns(
      CollectionTableColumn.ForecastComparison,
      "Demand",
      comparisonDemandGetter,
      forecastGetter,
    ),
    ...(returnsEnabled
      ? [
          createSumColumn({
            colId: CollectionTableColumn.ExpectedReturns,
            headerSubtitle: collectionLabel,
            minWidth: 150,
            valueGetter: (p: any) => maps()?.expectedReturnsMap?.get(p.data?.variantId) || 0,
            enableCellChangeFlash: false,
          }),
        ]
      : []),
    createSumColumn({
      colId: CollectionTableColumn.Sales,
      headerSubtitle: hasComparison
        ? `${collectionLabel} vs ${baselineComparisonName}`
        : collectionLabel,
      headerTooltip: isNoos
        ? `Gross ${formatName("sale", true)} for collection period. Limited to last 52 weeks (${formatDate(subWeeks(startOfTodayUTC(), 52), DateFormatType.FullDateShort)} – ${formatDate(startOfTodayUTC(), DateFormatType.FullDateShort)}).`
        : hasComparison
          ? `Gross ${formatName("sale", true)} for collection period. Percentage shows difference compared to ${baselineComparisonTooltipNames}.`
          : `Gross ${formatName("sale", true)} for collection period.`,
      headerInfoIcon: isNoos,
      minWidth: hasComparison ? 170 : 150,
      valueGetter: (p: any) => maps()?.salesMap?.get(p.data?.variantId)?.sales || 0,
      cellRenderer: makeMetricCellRenderer(
        (id) => maps()?.salesMap?.get(id)?.sales || 0,
        (id) => comparison()?.salesMap?.get(id)?.sales || 0,
        formatName("sale", true, true),
      ),
      tooltipComponent: hasComparison ? CollectionComparisonTooltip : undefined,
      tooltip: hasComparison
        ? makeMetricTooltipGetter(
            (id) => maps()?.salesMap?.get(id)?.sales || 0,
            (id) => comparison()?.salesMap?.get(id)?.sales || 0,
            formatName("sale", true, true),
          )
        : undefined,
      enableCellChangeFlash: false,
    }),
    ...makeComparisonColumns(
      CollectionTableColumn.SalesComparison,
      formatName("sale", true, true),
      salesGetter,
      (data, id) => data.salesMap?.get(id)?.sales ?? 0,
    ),
    createSumColumn({
      colId: CollectionTableColumn.PreOrders,
      headerSubtitle: hasComparison
        ? `${collectionLabel} vs ${baselineComparisonName}`
        : collectionLabel,
      headerTooltip: hasComparison
        ? `${formatName("preorder", true, true)} for collection period. Percentage shows difference compared to ${baselineComparisonTooltipNames}.`
        : `${formatName("preorder", true, true)} for collection period`,
      minWidth: hasComparison ? 170 : 150,
      valueGetter: (p: any) => preOrdersWithInboundGetter(maps(), p.data?.variantId),
      cellRenderer: makeMetricCellRenderer(
        (id) => preOrdersWithInboundGetter(maps(), id),
        (id) => preOrdersWithInboundGetter(comparison(), id),
        formatName("preorder", true, true),
      ),
      tooltipComponent: hasComparison ? CollectionComparisonTooltip : undefined,
      tooltip: hasComparison
        ? makeMetricTooltipGetter(
            (id) => preOrdersWithInboundGetter(maps(), id),
            (id) => preOrdersWithInboundGetter(comparison(), id),
            formatName("preorder", true, true),
          )
        : undefined,
      enableCellChangeFlash: false,
    }),
    ...makeComparisonColumns(
      CollectionTableColumn.PreOrdersComparison,
      formatName("preorder", true, true),
      preOrdersWithInboundGetter,
      (data, id) => preOrdersWithInboundGetter(data, id),
    ),
    createSumColumn({
      colId: CollectionTableColumn.FuturePreOrders,
      headerSubtitle: collectionLabel,
      headerTooltip: `Future ${formatName("preorder", true)} (not yet delivered) for collection period`,
      minWidth: 150,
      valueGetter: (p: any) => futurePreOrdersGetter(maps(), p.data?.variantId),
      cellRenderer: makeMetricCellRenderer(
        (id) => futurePreOrdersGetter(maps(), id),
        (id) => futurePreOrdersGetter(comparison(), id),
        `Future ${formatName("preorder", true)}`,
      ),
      enableCellChangeFlash: false,
    }),
    createSumColumn({
      colId: CollectionTableColumn.SalesRisk,
      headerSubtitle: hasComparison
        ? `${collectionLabel} vs ${baselineComparisonName}`
        : collectionLabel,
      headerTooltip: hasComparison
        ? `${formatName("sale", true, true)} minus ${formatName("preorder", true)} for collection period. Percentage shows difference compared to ${baselineComparisonTooltipNames}.`
        : `${formatName("sale", true, true)} minus ${formatName("preorder", true)} for collection period.`,
      minWidth: hasComparison ? 170 : 150,
      valueGetter: (p: any) => salesRiskGetter(maps(), p.data?.variantId),
      cellRenderer: makeMetricCellRenderer(
        (id) => salesRiskGetter(maps(), id),
        (id) => salesRiskGetter(comparison(), id),
        `${formatName("sale", true, true)} risk`,
      ),
      tooltipComponent: hasComparison ? CollectionComparisonTooltip : undefined,
      tooltip: hasComparison
        ? makeMetricTooltipGetter(
            (id) => salesRiskGetter(maps(), id),
            (id) => salesRiskGetter(comparison(), id),
            `${formatName("sale", true, true)} risk`,
          )
        : undefined,
      enableCellChangeFlash: false,
    }),
    ...makeComparisonColumns(
      CollectionTableColumn.SalesRiskComparison,
      `${formatName("sale", true, true)} risk`,
      salesRiskGetter,
      salesRiskGetter,
    ),
    createSumColumn({
      colId: CollectionTableColumn.SalesAmount,
      headerSubtitle: hasComparison
        ? `${collectionLabel} vs ${baselineComparisonName}`
        : collectionLabel,
      headerTooltip: hasComparison
        ? `Gross ${formatName("sale", true)} amount for collection period. Percentage shows difference compared to ${baselineComparisonTooltipNames}.`
        : `Gross ${formatName("sale", true)} amount for collection period.`,
      minWidth: hasComparison ? 170 : 150,
      valueGetter: (p: any) => maps()?.salesMap?.get(p.data?.variantId)?.salesAmount || 0,
      decimals: 1,
      suffix: currency,
      useFormatWithSuffix: true,
      cellRenderer: makeMetricCellRenderer(
        (id) => maps()?.salesMap?.get(id)?.salesAmount || 0,
        (id) => comparison()?.salesMap?.get(id)?.salesAmount || 0,
        `${formatName("sale", true, true)} amount`,
        (v) => `${formatNumberWithSuffix(v, 1)} ${currency}`,
      ),
      tooltipComponent: hasComparison ? CollectionComparisonTooltip : undefined,
      tooltip: hasComparison
        ? makeMetricTooltipGetter(
            (id) => maps()?.salesMap?.get(id)?.salesAmount || 0,
            (id) => comparison()?.salesMap?.get(id)?.salesAmount || 0,
            `${formatName("sale", true, true)} amount`,
          )
        : undefined,
      enableCellChangeFlash: false,
    }),
    createSumColumn({
      colId: CollectionTableColumn.PreOrdersAmount,
      headerSubtitle: hasComparison
        ? `${collectionLabel} vs ${baselineComparisonName}`
        : collectionLabel,
      headerTooltip: hasComparison
        ? `${formatName("preorder", true, true)} amount for collection period. Percentage shows difference compared to ${baselineComparisonTooltipNames}.`
        : `${formatName("preorder", true, true)} amount for collection period.`,
      minWidth: hasComparison ? 170 : 150,
      valueGetter: (p: any) => maps()?.preOrderMap?.get(p.data?.variantId)?.preOrderAmount || 0,
      decimals: 1,
      suffix: currency,
      useFormatWithSuffix: true,
      cellRenderer: makeMetricCellRenderer(
        (id) => maps()?.preOrderMap?.get(id)?.preOrderAmount || 0,
        (id) => comparison()?.preOrderMap?.get(id)?.preOrderAmount || 0,
        `${formatName("preorder", true, true)} amount`,
        (v) => `${formatNumberWithSuffix(v, 1)} ${currency}`,
      ),
      tooltipComponent: hasComparison ? CollectionComparisonTooltip : undefined,
      tooltip: hasComparison
        ? makeMetricTooltipGetter(
            (id) => maps()?.preOrderMap?.get(id)?.preOrderAmount || 0,
            (id) => comparison()?.preOrderMap?.get(id)?.preOrderAmount || 0,
            `${formatName("preorder", true, true)} amount`,
          )
        : undefined,
      enableCellChangeFlash: false,
    }),
    createSumColumn({
      colId: CollectionTableColumn.ProjectedSalesWithoutPreOrders,
      headerSubtitle: collectionLabel,
      valueGetter: (p: any) =>
        projectedSalesGetter(maps(), p.data?.variantId) -
        preOrdersGetter(maps(), p.data?.variantId),
      cellStyle: { textAlign: "right" },
      valueFormatter: (params: any) => (params.value ? formatNumber(params.value, 0) : "-"),
    }),
    createSumColumn({
      colId: CollectionTableColumn.ProjectedStockLevel,
      headerSubtitle: !hasComparison
        ? originalName
          ? `End of ${originalName}`
          : "End of collection period"
        : originalName,
      valueGetter: (params: any) =>
        Math.round(maps().closingStockMap?.get(params.data?.variantId) ?? 0),
      valueFormatter: (params: any) => formatNumber(params.value, 0),
      enableCellChangeFlash: false,
    }),
    createSumColumn({
      colId: CollectionTableColumn.ProjectedInventoryValue,
      valueGetter: (params: any) => {
        const costPrice = params.data?.costPrice ?? 0
        const stockLevel = Math.floor(maps().closingStockMap?.get(params.data?.variantId) ?? 0)
        return costPrice * stockLevel
      },
      decimals: 1,
      suffix: currency,
      useFormatWithSuffix: true,
      enableCellChangeFlash: false,
    }),
    createSumColumn({
      colId: CollectionTableColumn.Budget,
      headerTooltip: hasBudget
        ? "Estimated units based on budget targets"
        : "No budget associated with this collection",
      valueGetter: (p: any) => (hasBudget ? (live().budgetMap?.get(p.data?.variantId) ?? 0) : null),
      enableCellChangeFlash: false,
    }),
    createSumColumn({
      colId: CollectionTableColumn.IncomingOrders,
      headerSubtitle: hasComparison
        ? `${collectionLabel} vs ${baselineComparisonName}`
        : collectionLabel,
      headerTooltip: hasComparison
        ? `Purchase Orders for collection period. Percentage shows difference compared to ${baselineComparisonTooltipNames}.`
        : "Purchase Orders for collection period",
      minWidth: hasComparison ? 170 : 150,
      hasTooltip: false,
      valueGetter: (p: any) => maps()?.ordersMap?.get(p.data?.variantId) ?? 0,
      cellRenderer: makeMetricCellRenderer(
        (id) => maps()?.ordersMap?.get(id) ?? 0,
        (id) => comparison()?.ordersMap?.get(id) ?? 0,
        "Purchase Orders",
      ),
      tooltipComponent: IncomingOrdersCollectionTooltip,
      tooltip: (p) => {
        if (!p.node || (!p.data && !p.node.group)) return undefined
        if (
          !p.node.group &&
          p.data?.variantId &&
          comparison()?.matchedVariantsSet.has(p.data.variantId)
        )
          return undefined
        const variantIds = p.node?.group
          ? getFilteredLeafChildren(p.node).map((node) => node.data?.variantId ?? "")
          : [p.data?.variantId]

        const { maps, comparisonData } = live()
        if (hasComparison) {
          const baselineData = comparisonData[0]
          return {
            variantIds,
            isComparison: true,
            originalName: collectionLabel,
            comparisonName: baselineComparisonName,
            originalIncomingOrdersMap: maps.incomingOrdersMap,
            originalPurchaseOrdersMap: maps.purchaseOrdersMap,
            incomingOrdersMap: baselineData?.incomingOrdersMap,
            purchaseOrdersMap: baselineData?.purchaseOrdersMap,
          }
        }

        return {
          variantIds,
          incomingOrdersMap: maps.incomingOrdersMap,
          purchaseOrdersMap: maps.purchaseOrdersMap,
        }
      },
      enableCellChangeFlash: false,
    }),
    ...makeComparisonColumns(
      CollectionTableColumn.IncomingOrdersComparison,
      "Purchase Orders",
      ordersGetter,
      (data, id) => data.ordersMap?.get(id) ?? 0,
      (entryData) => ({
        hasTooltip: false,
        minWidth: 170,
        tooltipComponent: IncomingOrdersCollectionTooltip,
        tooltip: (p: any) => {
          if (!p.node || (!p.data && !p.node.group)) return undefined
          if (!p.node.group) return undefined
          const data = entryData()
          return {
            variantIds: getFilteredLeafChildren(p.node).map(
              (node: any) => node.data?.variantId ?? "",
            ),
            incomingOrdersMap: data?.incomingOrdersMap,
            purchaseOrdersMap: data?.purchaseOrdersMap,
          }
        },
      }),
    ),
  ]

  const hakioColumns: ColDef<IVariantSummary>[] = [
    createSumColumn({
      colId: CollectionTableColumn.InboundPreOrders,
      headerSubtitle: collectionLabel,
      valueGetter: (p: any) => {
        return maps()?.preOrderMap?.get(p.data?.variantId)?.inboundPreorderAmount || 0
      },
      enableCellChangeFlash: false,
    }),
    ...makeComparisonColumns(
      CollectionTableColumn.InboundPreOrdersComparison,
      `Inbound ${formatName("preorder", true)}`,
      inboundPreOrdersGetter,
      (data, id) => data.preOrderMap?.get(id)?.inboundPreorderAmount ?? 0,
    ),
    createSumColumn({
      colId: CollectionTableColumn.PreOrdersWithoutInbound,
      headerSubtitle: collectionLabel,
      valueGetter: (p: any) => {
        return maps()?.preOrderMap?.get(p.data?.variantId)?.preorders || 0
      },
      enableCellChangeFlash: false,
    }),
    ...makeComparisonColumns(
      CollectionTableColumn.PreOrdersWithoutInboundComparison,
      `${formatName("preorder", true, true)} without inbound`,
      preOrdersWithoutInboundGetter,
      (data, id) => data.preOrderMap?.get(id)?.preorders ?? 0,
    ),
  ]

  if (showInDepthColumns) {
    defaultColumns.push(...hakioColumns)
  }

  return defaultColumns
}

/**
 * `columns` stays stable across data updates: rebuilt only when the set of columns or their
 * labels change.
 */
export function useCollectionTableColumns({
  maps,
  comparisonMap,
  perCollectionComparisonData,
  comparisonCollections,
  showInDepthColumns,
  originalName,
  variantsMap,
  collectionIds,
  budgetMap,
  hasBudget,
  includeComparisonOnlyVariants = false,
  isNoos = false,
}: UseCollectionTableColumnsProps): VersionedColumns<IVariantSummary> {
  const firstComparison = comparisonCollections?.[0]
  const { data: company } = api.company.getCompany.useQuery()
  const currency = company?.currency ?? "DKK"
  const returnsEnabled = !!company?.returnsSettings?.enabled

  const hasComparisonCollections = (comparisonCollections?.length ?? 0) > 0
  const { data: fetchedVariantsMap } = api.collection.allVariantsForCollections.useQuery(
    {
      collectionIds: collectionIds ?? [],
      comparisonCollectionIds: comparisonCollections?.map((c) => c.id) ?? [],
    },
    {
      enabled:
        variantsMap === undefined && hasComparisonCollections && (collectionIds?.length ?? 0) > 0,
    },
  )
  const effectiveVariantsMap = variantsMap ?? fetchedVariantsMap

  const allSameGroupName =
    comparisonCollections !== undefined && comparisonCollections.length > 1
      ? comparisonCollections.every((c) => c.groupName === firstComparison?.groupName)
      : true

  const comparisonName =
    comparisonCollections !== undefined && comparisonCollections?.length > 1
      ? allSameGroupName && firstComparison?.groupName
        ? firstComparison.groupName
        : "Comparison"
      : (firstComparison?.name ?? "No comparison")

  const comparisonCollectionNames = comparisonCollections?.map((c) => c.name).join(", ")
  const hasComparison = comparisonCollections !== undefined && comparisonCollections.length > 0
  const normalizedComparisonData = useMemo((): PerCollectionComparisonEntry[] => {
    if (perCollectionComparisonData && perCollectionComparisonData.length > 0)
      return perCollectionComparisonData
    if (comparisonMap && hasComparison)
      return [{ id: 0, name: comparisonName, data: comparisonMap }]
    return []
  }, [perCollectionComparisonData, comparisonMap, hasComparison, comparisonName])

  const baselineComparisonTooltipNames =
    normalizedComparisonData.length > 1
      ? normalizedComparisonData[0]?.name
      : comparisonCollectionNames

  // Keyed by content: comparison entries and collections arrive as new objects on every refetch
  // for the same collections, which must not rebuild the columns.
  const comparisonEntriesKey = JSON.stringify(
    normalizedComparisonData.map(({ id, name }): ComparisonEntryMeta => ({ id, name })),
  )
  const comparisonCollectionIdsKey = JSON.stringify(comparisonCollections?.map((c) => c.id) ?? [])

  const liveData = useMemo(
    (): CollectionLiveData => ({
      maps,
      out: createOutColumnHelpers(maps),
      comparisonMap,
      comparisonData: normalizedComparisonData.map((entry) => entry.data),
      effectiveVariantsMap,
      budgetMap,
    }),
    [maps, comparisonMap, normalizedComparisonData, effectiveVariantsMap, budgetMap],
  )

  // AG Grid callbacks read the latest data through this reader when they evaluate, so a data
  // update leaves the column definitions untouched.
  const readLive = useLiveReader(liveData)

  const columns = useMemo(
    () =>
      buildCollectionColumns(
        {
          comparisonEntries: JSON.parse(comparisonEntriesKey) as ComparisonEntryMeta[],
          plannedCollectionIds: collectionIds,
          comparisonCollectionIds: JSON.parse(comparisonCollectionIdsKey) as number[],
          showInDepthColumns,
          comparisonName,
          originalName,
          baselineComparisonTooltipNames,
          currency,
          returnsEnabled,
          isNoos,
          hasBudget,
          includeComparisonOnlyVariants,
        },
        readLive,
      ),
    [
      readLive,
      comparisonEntriesKey,
      collectionIds,
      comparisonCollectionIdsKey,
      showInDepthColumns,
      comparisonName,
      originalName,
      currency,
      returnsEnabled,
      isNoos,
      hasBudget,
      baselineComparisonTooltipNames,
      includeComparisonOnlyVariants,
    ],
  )

  return { columns, dataVersion: liveData }
}
